import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"
import { AlertTriangle, Loader2, LogOut } from "lucide-react"

import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import {
  closeBillingPaywall,
  openBillingPaywall,
  selectBillingEntitlement,
  selectBillingPaywallDismissible,
  selectBillingPaywallOpen,
  selectBillingPortalUrl,
  setBillingEntitlement,
  setBillingPortalUrl,
} from "store/billing/billingSlice"
import { selectUserInfo } from "store/user/userSlice"
import {
  type StripeCatalogOffer,
  type StripeCheckoutStatusState,
  type StripeEntitlementResponse,
  type StripeErrorResponse,
} from "store/billing/types"
import { billingApi } from "services/billingApi"
import { typedEnv } from "@/config/typedEnv"
import {
  clearStoredCheckoutOperationId,
  clearStoredCheckoutSessionId,
  persistCheckoutOperationId,
  persistCheckoutSessionId,
  readStoredCheckoutOperationId,
  readStoredCheckoutSessionId,
} from "services/billingCheckoutStorage"
import { capturePlatformEvent } from "services/platformAnalytics"
import { syncBillingUnavailableState } from "services/platformBillingState"
import { tintVTOService } from "services/tintvto"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { BillingServiceUnavailable } from "./BillingServiceUnavailable"

type BillingCatalogStatus = "idle" | "loading" | "ready" | "error"

const BILLING_PORTAL_RETURN_QUERY_KEY = "billing_portal_return"
const STRIPE_RETURN_QUERY_KEYS = [
  "session_id",
  "checkout_session_id",
  "payment_intent",
  "redirect_status",
  "billing_cancelled",
  BILLING_PORTAL_RETURN_QUERY_KEY,
] as const
const readPositiveIntegerEnv = (value: string | undefined, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback
}

const CHECKOUT_STATUS_POLL_DELAYS_MS = [0, 1_500, 2_500, 4_000, 6_500, 10_000]
// Fired a beat after onboardingTrialEndsAt so the server-side clock has
// definitely crossed the boundary when we re-ask it.
const TRIAL_EXPIRY_SKEW_MS = 2_000
const MAX_TIMEOUT_DELAY_MS = 2 ** 31 - 1
const PORTAL_RETURN_POLL_INTERVAL_MS = readPositiveIntegerEnv(
  typedEnv.VITE_BILLING_PORTAL_RETURN_POLL_INTERVAL_MS,
  2_000
)
const PORTAL_RETURN_POLL_ATTEMPTS = readPositiveIntegerEnv(
  typedEnv.VITE_BILLING_PORTAL_RETURN_POLL_ATTEMPTS,
  30
)
const TERMINAL_PORTAL_RETURN_LOCK_REASONS = new Set<
  NonNullable<StripeEntitlementResponse["lockReason"]>
>(["no_subscription", "subscription_canceled", "trial_canceled"])
const PAYMENT_COPY = {
  checkoutAbandonedNotice: "Payment was canceled. You can try again anytime.",
  choosePlanTitle: "Choose a Plan",
  genericErrorTitle: "Something went wrong",
  invalidReturnNotice: "We couldn't finish that payment. Please try again.",
  managePlanError: "We couldn't open your plan page. Please try again.",
  noPlansDescription: "No plans are available right now. Please try again in a moment.",
  noPlansTitle: "No plans available",
  openCheckoutError: "We couldn't open the payment page. Please try again.",
} as const

type BillingGateProps = {
  children?: React.ReactNode
  enabled: boolean
}

const readCheckoutSessionIdFromSearch = (search: string) => {
  const query = new URLSearchParams(search)

  return query.get("session_id")?.trim() || query.get("checkout_session_id")?.trim() || ""
}

const hasBillingCancelFlag = (search: string) => {
  const query = new URLSearchParams(search)
  return query.get("billing_cancelled") === "1"
}

const hasBillingPortalReturnFlag = (search: string) => {
  const query = new URLSearchParams(search)
  return query.get(BILLING_PORTAL_RETURN_QUERY_KEY) === "1"
}

// Stripe appends these only after the shopper actually SUBMITTED a payment.
// A checkout abandoned via the browser Back button returns with a bare
// session_id and none of them.
const hasStripeSuccessReturnFlag = (search: string) => {
  const query = new URLSearchParams(search)
  return query.get("redirect_status") === "succeeded" || Boolean(query.get("payment_intent"))
}

const wait = (delayMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs)
  })

const getCheckoutStatusErrorMessage = (
  status: Exclude<StripeCheckoutStatusState, "completed" | "open" | "processing">
) => {
  switch (status) {
    case "expired":
      return "This payment page expired. Please try again."
    case "failed":
      return "Your payment didn't go through. Please try again."
  }
}

const formatPrice = (amount: number, currency: string) => {
  return `${Math.round(amount / 100)} ${currency.toUpperCase()}`
}

const isTrialFeature = (feature: string) => /free trial/i.test(feature)

const getVisibleFeatures = (offer: StripeCatalogOffer, showTrialMessaging: boolean) => {
  return showTrialMessaging
    ? offer.features
    : offer.features.filter((feature) => !isTrialFeature(feature))
}

const getBillingNote = (offer: StripeCatalogOffer, showTrialMessaging: boolean) => {
  const priceLabel = formatPrice(offer.amount, offer.currency)
  const intervalLabel =
    offer.interval === "month" && offer.intervalCount === 1
      ? "every 30 days"
      : offer.intervalCount === 1
      ? `every ${offer.interval}`
      : `every ${offer.intervalCount} ${offer.interval}s`

  const firstLine = showTrialMessaging
    ? `After the trial, you'll pay ${priceLabel} ${intervalLabel} until you cancel.`
    : `You'll pay ${priceLabel} ${intervalLabel} until you cancel.`

  return `${firstLine} You can cancel anytime.`
}

const readCheckoutStatusError = (error: unknown) => {
  if (!error || typeof error !== "object") return null

  const record = error as Record<string, unknown>
  const status = typeof record.status === "number" ? record.status : null
  const data =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : null
  const code = data && typeof data.code === "string" ? data.code : ""

  return {
    code,
    status,
  }
}

const isInvalidCheckoutReturnError = (error: unknown) => {
  const checkoutError = readCheckoutStatusError(error)
  if (!checkoutError) return false

  return checkoutError.status === 404 || checkoutError.code === "checkout_session_not_found"
}

const isTerminalDeniedPortalReturnEntitlement = (entitlement: StripeEntitlementResponse) =>
  !entitlement.entitled &&
  (entitlement.accessState === "inactive" ||
    entitlement.accessState === "locked" ||
    (entitlement.lockReason
      ? TERMINAL_PORTAL_RETURN_LOCK_REASONS.has(entitlement.lockReason)
      : false))

const isAmbiguousCheckoutResponse = (status: number) =>
  status === 408 || status === 425 || status === 429 || status >= 500

export const BillingGate: React.FC<BillingGateProps> = ({ children, enabled }) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const entitlement = useAppSelector(selectBillingEntitlement)
  const isPaywallDismissible = useAppSelector(selectBillingPaywallDismissible)
  const isPaywallOpen = useAppSelector(selectBillingPaywallOpen)
  const portalUrl = useAppSelector(selectBillingPortalUrl)
  const userInfo = useAppSelector(selectUserInfo)

  const [catalogOffers, setCatalogOffers] = useState<StripeCatalogOffer[]>([])
  const [isTrialEligible, setIsTrialEligible] = useState(true)
  const [catalogStatus, setCatalogStatusState] = useState<BillingCatalogStatus>("idle")
  const [isPortalSessionLoading, setIsPortalSessionLoading] = useState(false)
  const [activeCheckoutPriceId, setActiveCheckoutPriceId] = useState<string | null>(null)
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null)
  const [checkoutSyncState, setCheckoutSyncState] = useState<StripeCheckoutStatusState | null>(null)
  const [isPortalReturnSyncing, setIsPortalReturnSyncing] = useState(false)
  const [checkoutStatusNotice, setCheckoutStatusNotice] = useState("")
  const [billingSyncRetryKey, setBillingSyncRetryKey] = useState(0)
  const [errorMessage, setErrorMessage] = useState("")
  const [storedCheckoutSnapshot, setStoredCheckoutSnapshot] = useState<{
    ownerKey: string
    sessionId: string | null
  }>({ ownerKey: "", sessionId: null })
  // The billing backbone is unreachable (entitlement or catalog fetch
  // failed): the gate cannot decide anything, so instead of a broken paywall
  // it shows the full-screen "we'll be right back" interstitial.
  const [isBillingUnavailable, setIsBillingUnavailable] = useState(false)
  const [isOutageRetrying, setIsOutageRetrying] = useState(false)
  const checkoutOperationIdsRef = useRef(new Map<string, string>())
  const checkoutInFlightRef = useRef(false)
  const staleEntitlementConfirmedRef = useRef(false)
  const catalogStatusRef = useRef<BillingCatalogStatus>("idle")
  const paywallViewedRef = useRef(false)

  const shouldRenderGate = enabled
  const currentSearch = searchParams.toString()
  const locationSearch = currentSearch ? `?${currentSearch}` : ""
  const currentReturnPath = `${location.pathname}${location.search}`
  const checkoutSessionOwner = useMemo(
    () => ({
      merchantId: userInfo.merchantId,
      username: userInfo.username,
    }),
    [userInfo.merchantId, userInfo.username]
  )
  const checkoutSessionOwnerKey = `${checkoutSessionOwner.merchantId}\u0000${checkoutSessionOwner.username}`
  const isCheckoutStoragePending = storedCheckoutSnapshot.ownerKey !== checkoutSessionOwnerKey
  const storedCheckoutSessionId = isCheckoutStoragePending ? null : storedCheckoutSnapshot.sessionId
  const hasCheckoutReturnSession = Boolean(readCheckoutSessionIdFromSearch(locationSearch))
  const hasPortalReturn = hasBillingPortalReturnFlag(locationSearch)
  const requiresPortal = entitlement?.requiresPortal ?? false
  const isTrialCanceled = entitlement?.lockReason === "trial_canceled"
  const showTrialMessaging = isTrialEligible && !requiresPortal && !isTrialCanceled
  const isCheckoutStatusPolling = Boolean(checkoutSessionId)
  const isExternalBillingFlowActive = Boolean(
    isCheckoutStoragePending ||
      checkoutSessionId ||
      storedCheckoutSessionId ||
      hasCheckoutReturnSession ||
      hasPortalReturn ||
      isPortalReturnSyncing
  )
  const isBillingSyncVisible =
    shouldRenderGate &&
    !entitlement?.entitled &&
    isExternalBillingFlowActive &&
    (isCheckoutStatusPolling ||
      isPortalReturnSyncing ||
      checkoutSyncState === "processing" ||
      checkoutSyncState === "failed" ||
      Boolean(checkoutStatusNotice))
  const isCatalogLoading = catalogStatus === "loading"
  const needsCatalogBeforeOpen =
    shouldRenderGate &&
    isPaywallOpen &&
    !requiresPortal &&
    !isCheckoutStatusPolling &&
    !isBillingSyncVisible
  const isPreparingPaywall =
    needsCatalogBeforeOpen && (catalogStatus === "idle" || catalogStatus === "loading")
  const isPaywallVisible = isPaywallOpen && !isPreparingPaywall && !isBillingSyncVisible

  const setCatalogStatus = useCallback((status: BillingCatalogStatus) => {
    catalogStatusRef.current = status
    setCatalogStatusState(status)
  }, [])

  const clearCheckoutSessionStorage = useCallback(() => {
    clearStoredCheckoutSessionId()
    setStoredCheckoutSnapshot({ ownerKey: checkoutSessionOwnerKey, sessionId: null })
  }, [checkoutSessionOwnerKey])

  const persistCheckoutSessionStorage = useCallback(
    (sessionId: string) => {
      persistCheckoutSessionId(sessionId, checkoutSessionOwner)
      setStoredCheckoutSnapshot({ ownerKey: checkoutSessionOwnerKey, sessionId })
    },
    [checkoutSessionOwner, checkoutSessionOwnerKey]
  )

  const resetCatalogState = useCallback(() => {
    setCatalogOffers([])
    setCatalogStatus("idle")
    setIsTrialEligible(true)
    setActiveCheckoutPriceId(null)
  }, [setCatalogStatus])

  const clearStripeReturnParams = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams)
    let changed = false

    STRIPE_RETURN_QUERY_KEYS.forEach((key) => {
      if (!nextSearchParams.has(key)) return
      nextSearchParams.delete(key)
      changed = true
    })

    if (!changed) return
    setSearchParams(nextSearchParams, {
      replace: true,
    })
  }, [searchParams, setSearchParams])

  const resetCheckoutReturnState = useCallback(
    (input?: {
      errorMessage?: string
      notice?: string
      syncState?: StripeCheckoutStatusState | null
    }) => {
      clearCheckoutSessionStorage()
      clearStripeReturnParams()
      setIsPortalReturnSyncing(false)
      setCheckoutSessionId(null)
      setCheckoutSyncState(input?.syncState ?? null)
      setCheckoutStatusNotice(input?.notice ?? "")

      if (input && "errorMessage" in input) setErrorMessage(input.errorMessage ?? "")
    },
    [clearCheckoutSessionStorage, clearStripeReturnParams]
  )

  const keepBillingSyncState = useCallback(
    (input: { notice: string; syncState?: StripeCheckoutStatusState }) => {
      setCheckoutSyncState(input.syncState ?? "processing")
      setCheckoutStatusNotice(input.notice)
      setErrorMessage("")
      dispatch(closeBillingPaywall())
    },
    [dispatch]
  )

  const refreshEntitlement = useCallback(
    async (autoOpenPaywall: boolean) => {
      if (!shouldRenderGate) {
        dispatch(closeBillingPaywall())
        return
      }

      try {
        const nextEntitlement = await billingApi.stripeEntitlement()
        setErrorMessage("")
        setIsBillingUnavailable(false)

        if (nextEntitlement.entitled) {
          resetCheckoutReturnState({
            errorMessage: "",
            notice: "",
            syncState: "completed",
          })
          dispatch(closeBillingPaywall())
          return
        }

        if (autoOpenPaywall) dispatch(openBillingPaywall())
      } catch (error) {
        // We could not determine access at all — that is OUR problem, not the
        // user's. Never open a paywall we cannot back with data; show the
        // service interstitial instead.
        syncBillingUnavailableState()
        setIsBillingUnavailable(true)
        dispatch(closeBillingPaywall())
      }
    },
    [dispatch, resetCheckoutReturnState, shouldRenderGate]
  )

  const retryBillingAvailability = useCallback(async () => {
    setIsOutageRetrying(true)
    setErrorMessage("")
    resetCatalogState()

    try {
      // Success clears the interstitial and re-runs the normal gate flow
      // (paywall reopens and reloads the catalog); another failure re-arms it.
      await refreshEntitlement(!isExternalBillingFlowActive)
    } finally {
      setIsOutageRetrying(false)
    }
  }, [isExternalBillingFlowActive, refreshEntitlement, resetCatalogState])

  const recoverInvalidCheckoutReturnState = useCallback(async () => {
    resetCheckoutReturnState({
      errorMessage: "",
      notice: PAYMENT_COPY.invalidReturnNotice,
      syncState: null,
    })
    dispatch(openBillingPaywall())
    await refreshEntitlement(false)
  }, [dispatch, refreshEntitlement, resetCheckoutReturnState])

  const retryBillingSync = useCallback(() => {
    const shouldRetryPortalReturn = hasPortalReturn || isPortalReturnSyncing
    const nextCheckoutSessionId = shouldRetryPortalReturn
      ? ""
      : checkoutSessionId || storedCheckoutSessionId

    setErrorMessage("")
    setCheckoutSyncState("processing")

    if (shouldRetryPortalReturn) {
      clearCheckoutSessionStorage()
      setCheckoutSessionId(null)
      setIsPortalReturnSyncing(true)
      setCheckoutStatusNotice("We're syncing your payment status. This can take a moment.")
    } else if (nextCheckoutSessionId) {
      setCheckoutSessionId(nextCheckoutSessionId)
      setIsPortalReturnSyncing(false)
      setCheckoutStatusNotice("We're checking your payment. This can take a moment.")
    } else {
      setCheckoutSessionId(null)
      setIsPortalReturnSyncing(true)
      setCheckoutStatusNotice("We're syncing your payment status. This can take a moment.")
    }

    dispatch(closeBillingPaywall())
    setBillingSyncRetryKey((current) => current + 1)
  }, [
    checkoutSessionId,
    clearCheckoutSessionStorage,
    dispatch,
    hasPortalReturn,
    isPortalReturnSyncing,
    storedCheckoutSessionId,
  ])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      // A bfcache restore (browser Back from Stripe) resurrects the pre-checkout
      // in-memory state: sync gate up, status poll already finished. Re-run the
      // sync so an abandoned session resolves to the paywall instead of an
      // eternal "Payment syncing" screen.
      setBillingSyncRetryKey((current) => current + 1)
    }

    window.addEventListener("pageshow", handlePageShow)
    return () => window.removeEventListener("pageshow", handlePageShow)
  }, [])

  // The onboarding trial is only an hour long (Asana 1217267783347632): the
  // paywall must appear when it expires, not on the merchant's next reload or
  // API action. At trialEndsAt we re-ask the server — the fresh entitlement
  // comes back expired/not-entitled and the normal gate flow opens the
  // paywall. Strictly requires onboardingTrialEndsAt: entitlements without it
  // (subscriptions, admin bypass) never arm the timer.
  useEffect(() => {
    if (!shouldRenderGate || !entitlement?.entitled) return
    if (entitlement.onboardingTrialStatus !== "active") return
    const endsAtEpochSeconds = entitlement.onboardingTrialEndsAt
    if (!endsAtEpochSeconds) return

    const delayMs = Math.min(
      Math.max(0, endsAtEpochSeconds * 1_000 - Date.now()) + TRIAL_EXPIRY_SKEW_MS,
      MAX_TIMEOUT_DELAY_MS
    )
    const timer = window.setTimeout(() => {
      void refreshEntitlement(true)
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [
    entitlement?.entitled,
    entitlement?.onboardingTrialEndsAt,
    entitlement?.onboardingTrialStatus,
    refreshEntitlement,
    shouldRenderGate,
  ])

  const startPortalSession = useCallback(async () => {
    if (!shouldRenderGate) return

    try {
      setIsPortalSessionLoading(true)
      setErrorMessage("")

      let nextPortalUrl = portalUrl
      if (!nextPortalUrl) {
        const session = await billingApi.stripePortalSession({
          returnPath: currentReturnPath,
        })
        nextPortalUrl = session.url
        dispatch(setBillingPortalUrl(nextPortalUrl))
      }

      if (!nextPortalUrl) {
        setErrorMessage(PAYMENT_COPY.managePlanError)
        return
      }

      dispatch(closeBillingPaywall())
      window.location.assign(nextPortalUrl)
    } catch (error) {
      setErrorMessage(PAYMENT_COPY.managePlanError)
    } finally {
      setIsPortalSessionLoading(false)
    }
  }, [currentReturnPath, dispatch, portalUrl, shouldRenderGate])

  const startCheckoutSession = useCallback(
    async (offer: StripeCatalogOffer) => {
      if (checkoutInFlightRef.current) return
      checkoutInFlightRef.current = true

      try {
        setActiveCheckoutPriceId(offer.priceId)
        setErrorMessage("")
        setCheckoutStatusNotice("")
        capturePlatformEvent("pricing_plan_selected", {
          billing_interval: offer.interval,
          billing_interval_count: offer.intervalCount,
          currency: offer.currency,
          price_amount: offer.amount,
          price_id: offer.priceId,
          product_id: offer.productId,
          trial_eligible: showTrialMessaging,
        })

        const operationKey = JSON.stringify([
          checkoutSessionOwner.username,
          checkoutSessionOwner.merchantId,
          offer.priceId,
        ])
        const operationId =
          checkoutOperationIdsRef.current.get(operationKey) ||
          readStoredCheckoutOperationId(offer.priceId, checkoutSessionOwner) ||
          crypto.randomUUID()
        checkoutOperationIdsRef.current.set(operationKey, operationId)
        persistCheckoutOperationId(operationId, offer.priceId, checkoutSessionOwner)

        const clearCheckoutOperation = () => {
          if (checkoutOperationIdsRef.current.get(operationKey) === operationId)
            checkoutOperationIdsRef.current.delete(operationKey)
          clearStoredCheckoutOperationId(operationId, offer.priceId, checkoutSessionOwner)
        }

        const result = await billingApi.stripeCheckoutSession(offer.priceId, {
          operationId,
          returnPath: currentReturnPath,
        })
        if (result.ok) {
          capturePlatformEvent("stripe_checkout_accepted", {
            billing_interval: offer.interval,
            billing_interval_count: offer.intervalCount,
            price_id: offer.priceId,
            product_id: offer.productId,
            stripe_checkout_session_id: result.data.sessionId,
            trial_eligible: showTrialMessaging,
          })
          persistCheckoutSessionStorage(result.data.sessionId)
          setCheckoutSessionId(result.data.sessionId)
          dispatch(closeBillingPaywall())
          clearCheckoutOperation()
          try {
            window.location.assign(result.data.url)
          } catch (error) {
            checkoutOperationIdsRef.current.set(operationKey, operationId)
            persistCheckoutOperationId(operationId, offer.priceId, checkoutSessionOwner)
            throw error
          }
          return
        }

        const errorData = result.data as StripeErrorResponse | null
        if (errorData?.code === "billing_already_active") {
          clearCheckoutOperation()
          if (errorData.entitlement) dispatch(setBillingEntitlement(errorData.entitlement))
          if (errorData.portalUrl) dispatch(setBillingPortalUrl(errorData.portalUrl))

          if (errorData.entitlement?.entitled) {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: "completed",
            })
            dispatch(closeBillingPaywall())
          }

          return
        }

        if (errorData?.code === "billing_portal_required") {
          clearCheckoutOperation()
          return
        }

        if (errorData?.code === "checkout_session_restart_required") {
          clearCheckoutOperation()
          setErrorMessage(PAYMENT_COPY.openCheckoutError)
          return
        }

        if (errorData?.code === "checkout_session_conflict") {
          // The backend could not prove that the Stripe session belongs to a
          // safely reusable or terminal local attempt. Fail closed instead of
          // rotating the key and risking a second subscription session.
          syncBillingUnavailableState()
          setIsBillingUnavailable(true)
          return
        }

        // A retryable or otherwise ambiguous response can be emitted after
        // Stripe accepted the request but before local persistence completed.
        // Keep the operation id in that case so the next click (or reload)
        // reaches Stripe with the same idempotency key.
        if (!isAmbiguousCheckoutResponse(result.status)) clearCheckoutOperation()

        setErrorMessage(PAYMENT_COPY.openCheckoutError)
        return
      } catch (error) {
        setErrorMessage(PAYMENT_COPY.openCheckoutError)
      } finally {
        checkoutInFlightRef.current = false
        setActiveCheckoutPriceId(null)
      }
    },
    [
      checkoutSessionOwner,
      currentReturnPath,
      dispatch,
      persistCheckoutSessionStorage,
      resetCheckoutReturnState,
      showTrialMessaging,
    ]
  )

  useEffect(() => {
    let canceled = false

    const loadStoredCheckout = async () => {
      // Keep Web Storage I/O outside render. The microtask also prevents a
      // synchronous effect-state cascade while preserving owner fencing.
      await Promise.resolve()
      const sessionId = readStoredCheckoutSessionId(checkoutSessionOwner)
      if (canceled) return
      setStoredCheckoutSnapshot({ ownerKey: checkoutSessionOwnerKey, sessionId })
    }

    void loadStoredCheckout()
    return () => {
      canceled = true
    }
  }, [checkoutSessionOwner, checkoutSessionOwnerKey])

  useEffect(() => {
    if (!shouldRenderGate) {
      setErrorMessage("")
      setCheckoutStatusNotice("")
      resetCheckoutReturnState({
        errorMessage: "",
        notice: "",
        syncState: null,
      })
      resetCatalogState()
      dispatch(closeBillingPaywall())
      return
    }

    if (entitlement?.entitled) {
      // A dismissible paywall is a user-opened plan catalog — an entitled
      // trial merchant browsing plans keeps it until they close it.
      if (isPaywallDismissible) return
      resetCheckoutReturnState({
        errorMessage: "",
        notice: "",
        syncState: "completed",
      })
      dispatch(closeBillingPaywall())
      return
    }

    if (entitlement && !entitlement.entitled) {
      if (isExternalBillingFlowActive) return
      // A not-entitled snapshot with status "none" and no experiment fields
      // is the signature of a billing row that is not written yet (the
      // registration race QA hit on Asana 1217267783347632). Confirm once
      // with a fresh read before locking the workspace; a genuinely blocked
      // merchant comes back unchanged and the paywall opens below.
      if (
        entitlement.entitlementStatus === "none" &&
        !entitlement.onboardingFlowVariant &&
        !staleEntitlementConfirmedRef.current
      ) {
        staleEntitlementConfirmedRef.current = true
        void refreshEntitlement(true)
        return
      }
      dispatch(openBillingPaywall())
      return
    }

    void refreshEntitlement(!isExternalBillingFlowActive)
  }, [
    dispatch,
    entitlement,
    isExternalBillingFlowActive,
    isPaywallDismissible,
    refreshEntitlement,
    resetCatalogState,
    resetCheckoutReturnState,
    shouldRenderGate,
  ])

  useEffect(() => {
    if (!shouldRenderGate) return

    if (hasBillingCancelFlag(locationSearch)) {
      resetCheckoutReturnState({
        errorMessage: "",
        notice: PAYMENT_COPY.checkoutAbandonedNotice,
        syncState: null,
      })
      dispatch(openBillingPaywall())
      return
    }

    if (hasPortalReturn) {
      if (entitlement?.entitled) {
        resetCheckoutReturnState({
          errorMessage: "",
          notice: "",
          syncState: "completed",
        })
        dispatch(closeBillingPaywall())
        return
      }

      clearCheckoutSessionStorage()
      setCheckoutSessionId(null)
      setCheckoutSyncState("processing")
      setIsPortalReturnSyncing(true)
      setCheckoutStatusNotice("We're syncing your payment status. This can take a moment.")
      setErrorMessage("")
      dispatch(closeBillingPaywall())
      return
    }

    if (hasCheckoutReturnSession) {
      const returnSessionId = readCheckoutSessionIdFromSearch(locationSearch)
      persistCheckoutSessionStorage(returnSessionId)
      setCheckoutSessionId(returnSessionId || null)
      setCheckoutSyncState("processing")
      setIsPortalReturnSyncing(false)
      setCheckoutStatusNotice("We're checking your payment. This can take a moment.")
      setErrorMessage("")
      dispatch(closeBillingPaywall())
      return
    }

    if (!storedCheckoutSessionId || entitlement?.entitled) return

    setCheckoutSessionId((currentSessionId) => currentSessionId ?? storedCheckoutSessionId)
  }, [
    dispatch,
    entitlement?.entitled,
    hasCheckoutReturnSession,
    hasPortalReturn,
    checkoutSessionOwner,
    clearCheckoutSessionStorage,
    locationSearch,
    persistCheckoutSessionStorage,
    resetCheckoutReturnState,
    shouldRenderGate,
    storedCheckoutSessionId,
  ])

  useEffect(() => {
    if (!isPaywallVisible) {
      paywallViewedRef.current = false
      return
    }

    if (paywallViewedRef.current) return
    paywallViewedRef.current = true

    capturePlatformEvent("paywall_viewed", {
      access_state: entitlement?.accessState,
      // dismissible=true is a user-opened plan catalog, not the access gate —
      // keep the two separable in analytics.
      dismissible: isPaywallDismissible,
      entitlement_status: entitlement?.entitlementStatus,
      lock_reason: entitlement?.lockReason,
      onboarding_flow_variant: entitlement?.onboardingFlowVariant,
      onboarding_trial_status: entitlement?.onboardingTrialStatus,
      requires_portal: requiresPortal,
      trial_eligible: isTrialEligible,
    })
  }, [
    entitlement?.accessState,
    entitlement?.entitlementStatus,
    entitlement?.lockReason,
    entitlement?.onboardingFlowVariant,
    entitlement?.onboardingTrialStatus,
    isPaywallDismissible,
    isPaywallVisible,
    isTrialEligible,
    requiresPortal,
  ])

  useEffect(() => {
    if (!shouldRenderGate || !isPortalReturnSyncing || entitlement?.entitled) return

    let canceled = false

    const syncPortalReturn = async () => {
      setCheckoutStatusNotice("We're syncing your payment status. This can take a moment.")
      setErrorMessage("")

      try {
        let lastEntitlement: StripeEntitlementResponse | null = null

        for (let attempt = 0; attempt < PORTAL_RETURN_POLL_ATTEMPTS; attempt += 1) {
          if (attempt > 0) await wait(PORTAL_RETURN_POLL_INTERVAL_MS)
          if (canceled) return

          const nextEntitlement = await billingApi.stripeEntitlement({
            suppressPaywall: true,
          })
          if (canceled) return

          lastEntitlement = nextEntitlement

          if (nextEntitlement.entitled) {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: "completed",
            })
            dispatch(closeBillingPaywall())
            return
          }

          if (isTerminalDeniedPortalReturnEntitlement(nextEntitlement)) {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: null,
            })
            dispatch(openBillingPaywall())
            return
          }
        }

        if (canceled) return

        if (lastEntitlement && !lastEntitlement.entitled) {
          resetCheckoutReturnState({
            errorMessage: "",
            notice: "",
            syncState: null,
          })
          dispatch(openBillingPaywall())
          return
        }

        setCheckoutSyncState("failed")
        setCheckoutStatusNotice("We couldn't sync your billing status yet. Please try again.")
        setErrorMessage("We couldn't sync your billing status yet. Please try again.")
        dispatch(closeBillingPaywall())
      } catch (error) {
        if (canceled) return

        setCheckoutSyncState("failed")
        setCheckoutStatusNotice("We couldn't sync your billing status yet. Please try again.")
        setErrorMessage("We couldn't sync your billing status yet. Please try again.")
        dispatch(closeBillingPaywall())
      }
    }

    void syncPortalReturn()

    return () => {
      canceled = true
    }
  }, [
    dispatch,
    entitlement?.entitled,
    isPortalReturnSyncing,
    resetCheckoutReturnState,
    billingSyncRetryKey,
    shouldRenderGate,
  ])

  useEffect(() => {
    if (!shouldRenderGate || !checkoutSessionId || entitlement?.entitled) return

    let canceled = false
    // With a success return marker (redirect_status/payment_intent) a payment
    // WAS submitted, so a briefly-"open" session is just finalization lag and
    // must keep polling. Without one — the browser-Back case — "open" means
    // no payment was ever sent and will never settle.
    const paymentWasSubmitted = hasStripeSuccessReturnFlag(locationSearch)

    const syncCheckoutStatus = async () => {
      setCheckoutSyncState("processing")
      setCheckoutStatusNotice("We're checking your payment. This can take a moment.")
      setErrorMessage("")

      try {
        // Finalize the paid checkout without waiting on Stripe webhook delivery:
        // the backend retrieves the session and, if paid, writes entitlement now.
        // On success we're done; otherwise fall into the status poll below (which
        // covers async payment settlement and transient finalize failures).
        try {
          const finalized = await billingApi.stripeCheckoutFinalize(checkoutSessionId, {
            suppressPaywall: true,
          })
          if (canceled) return

          setCheckoutSyncState(finalized.status)
          if (finalized.status === "completed") {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: "completed",
            })
            dispatch(closeBillingPaywall())
            return
          }
          if (finalized.status === "open" && !paymentWasSubmitted) {
            // The user left Stripe Checkout without submitting payment (e.g.
            // browser Back): nothing will ever settle, so waiting would trap
            // them behind the sync gate. Drop the stored session and show the
            // paywall, same as the explicit cancel return.
            resetCheckoutReturnState({
              errorMessage: "",
              notice: PAYMENT_COPY.checkoutAbandonedNotice,
              syncState: null,
            })
            dispatch(openBillingPaywall())
            return
          }
        } catch (finalizeError) {
          if (canceled) return
          // A genuinely invalid/foreign session must still trigger recovery.
          if (isInvalidCheckoutReturnError(finalizeError)) throw finalizeError
          // Otherwise ignore and fall through to the poll.
        }

        let lastKnownStatus: StripeCheckoutStatusState = "processing"
        // A "processing" report proves a payment reached Stripe even when the
        // return URL carries no success marker (e.g. a reload re-armed the
        // session from storage after the params were stripped).
        let sawProcessing = false

        for (const delayMs of CHECKOUT_STATUS_POLL_DELAYS_MS) {
          if (delayMs > 0) await wait(delayMs)
          if (canceled) return

          const nextStatus = await billingApi.stripeCheckoutStatus(checkoutSessionId, {
            suppressPaywall: true,
          })
          if (canceled) return

          lastKnownStatus = nextStatus.status
          if (nextStatus.status === "processing") sawProcessing = true
          setCheckoutSyncState(nextStatus.status)

          if (nextStatus.status === "completed") {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: "completed",
            })
            dispatch(closeBillingPaywall())
            return
          }

          if (nextStatus.status === "open") {
            if (!paymentWasSubmitted && !sawProcessing) {
              // No success marker and never seen processing: the shopper left
              // Stripe Checkout without submitting payment (browser Back).
              // Nothing will ever settle — terminal for this return.
              resetCheckoutReturnState({
                errorMessage: "",
                notice: PAYMENT_COPY.checkoutAbandonedNotice,
                syncState: null,
              })
              dispatch(openBillingPaywall())
              return
            }
            // A submitted payment briefly reported "open" is finalization
            // lag — treat like "processing" and keep polling.
            continue
          }

          if (nextStatus.status !== "processing") {
            resetCheckoutReturnState({
              errorMessage: getCheckoutStatusErrorMessage(nextStatus.status),
              notice: "",
              syncState: nextStatus.status,
            })
            dispatch(openBillingPaywall())
            return
          }
        }

        if (canceled || (lastKnownStatus !== "processing" && lastKnownStatus !== "open")) return

        keepBillingSyncState({
          notice: "We're still checking your payment. Your access will update soon.",
          syncState: "processing",
        })
      } catch (error) {
        if (canceled) return

        if (isInvalidCheckoutReturnError(error)) {
          await recoverInvalidCheckoutReturnState()
          return
        }

        setCheckoutSyncState("failed")
        setCheckoutStatusNotice("We couldn't check your payment yet. Please try again.")
        setErrorMessage("We couldn't check your payment yet. Please try again.")
        dispatch(closeBillingPaywall())
      }
    }

    void syncCheckoutStatus()

    return () => {
      canceled = true
    }
  }, [
    checkoutSessionId,
    dispatch,
    entitlement?.entitled,
    keepBillingSyncState,
    recoverInvalidCheckoutReturnState,
    resetCheckoutReturnState,
    billingSyncRetryKey,
    shouldRenderGate,
  ])

  useEffect(() => {
    if (!isPaywallOpen || !shouldRenderGate) return
    if (requiresPortal) {
      setCatalogStatus("idle")
      return
    }
    if (catalogStatusRef.current === "ready") return

    let canceled = false

    const loadCatalog = async () => {
      setErrorMessage("")
      resetCatalogState()
      setCatalogStatus("loading")

      try {
        const catalog = await billingApi.stripeCatalog()
        if (canceled) return

        setCatalogOffers(catalog.offers)
        setCatalogStatus("ready")
        setIsTrialEligible(!isTrialCanceled && catalog.trialEligible)
        setIsBillingUnavailable(false)
      } catch (error) {
        if (canceled) return

        // No plans data = nothing meaningful to render in the paywall. Close
        // it and hand over to the full-screen interstitial.
        syncBillingUnavailableState()
        setCatalogStatus("error")
        setIsBillingUnavailable(true)
        dispatch(closeBillingPaywall())
      }
    }

    void loadCatalog()

    return () => {
      canceled = true
    }
  }, [
    dispatch,
    isPaywallOpen,
    isTrialCanceled,
    requiresPortal,
    resetCatalogState,
    setCatalogStatus,
    shouldRenderGate,
  ])

  if (!shouldRenderGate) return <>{children}</>

  if (isBillingUnavailable)
    return (
      <BillingServiceUnavailable
        isRetrying={isOutageRetrying}
        onLogout={() => tintVTOService.logout()}
        onRetry={() => void retryBillingAvailability()}
      />
    )

  return (
    <>
      {entitlement?.entitled ? children : null}

      <Dialog open={isBillingSyncVisible}>
        <DialogContent
          showClose={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          className="w-[min(92vw,28rem)] rounded-2xl border-border bg-background p-6 shadow-2xl"
          data-testid="billing-sync-dialog"
        >
          <DialogHeader className="gap-2 text-left">
            <DialogTitle>
              {checkoutSyncState === "failed" ? "Payment status unavailable" : "Payment syncing"}
            </DialogTitle>
            <DialogDescription
              className="text-sm leading-6 text-muted-foreground"
              data-testid="billing-sync-notice"
              role="status"
              aria-live="polite"
            >
              {checkoutStatusNotice || "We're checking your payment. This can take a moment."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {checkoutSyncState === "failed" ? (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertTitle>{PAYMENT_COPY.genericErrorTitle}</AlertTitle>
                <AlertDescription>
                  {errorMessage || "We couldn't check your payment yet. Please try again."}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="flex items-center gap-3 rounded-xl border border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground">
                <Loader2 className="size-4 shrink-0 animate-spin" />
                <span>Your workspace will reopen when Stripe finishes syncing.</span>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={retryBillingSync}
              className="w-fit"
              data-testid="billing-sync-retry"
            >
              Check again
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isPaywallVisible}
        onOpenChange={(open) => {
          if (!open && isPaywallDismissible) dispatch(closeBillingPaywall())
        }}
      >
        <DialogContent
          showClose={isPaywallDismissible}
          aria-describedby={undefined}
          onEscapeKeyDown={(event) => {
            if (!isPaywallDismissible) event.preventDefault()
          }}
          onInteractOutside={(event) => {
            if (!isPaywallDismissible) event.preventDefault()
          }}
          className="flex max-h-[calc(100dvh-1rem)] w-[96vw] overflow-hidden border-0 bg-transparent p-0 shadow-none sm:max-h-[calc(100dvh-1.5rem)] sm:max-w-[72rem]"
          data-testid="billing-paywall-dialog"
        >
          <div className="flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-3xl bg-background px-4 py-4 shadow-2xl sm:px-5 sm:py-5">
            <DialogHeader className="mb-4 shrink-0 flex-row items-start justify-between gap-4 border-b pb-4 text-left">
              <DialogTitle>{PAYMENT_COPY.choosePlanTitle}</DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => tintVTOService.logout()}
                >
                  <LogOut data-icon="inline-start" />
                  Log out
                </Button>
              </div>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain pr-1">
              {!isCatalogLoading && requiresPortal && (
                <Card className="rounded-2xl border-border/70 shadow-lg">
                  <CardHeader className="flex flex-col gap-3">
                    <Alert variant="destructive">
                      <AlertTriangle className="size-4" />
                      <AlertTitle>Action needed</AlertTitle>
                      <AlertDescription>
                        Please update your payment details before you continue.
                      </AlertDescription>
                    </Alert>
                    <CardDescription className="leading-6">
                      Open the plan page, make the needed changes, and come back here.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3 text-sm text-muted-foreground">
                    <Card className="shadow-none">
                      <CardHeader className="gap-1 p-4">
                        <CardTitle className="text-sm">Payment issue</CardTitle>
                        <CardDescription>Please update your payment details.</CardDescription>
                      </CardHeader>
                    </Card>
                    <Card className="shadow-none">
                      <CardHeader className="gap-1 p-4">
                        <CardTitle className="text-sm">Plan paused</CardTitle>
                        <CardDescription>
                          Turn your plan back on to keep using the app.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                    <Card className="shadow-none">
                      <CardHeader className="gap-1 p-4">
                        <CardTitle className="text-sm">One more step</CardTitle>
                        <CardDescription>
                          Please confirm your payment or update your details.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </CardContent>
                  <CardFooter className="flex items-center gap-3">
                    <Button
                      type="button"
                      onClick={() => void startPortalSession()}
                      disabled={isPortalSessionLoading}
                    >
                      {isPortalSessionLoading && (
                        <Loader2 data-icon="inline-start" className="animate-spin" />
                      )}
                      Open plan page
                    </Button>
                  </CardFooter>
                </Card>
              )}

              {!isCatalogLoading && !requiresPortal && catalogOffers.length > 0 && (
                <div className="grid gap-5 md:grid-cols-3">
                  {catalogOffers.map((offer) => {
                    const isCheckingOut = activeCheckoutPriceId === offer.priceId
                    const visibleFeatures = getVisibleFeatures(offer, showTrialMessaging)
                    const billingNote = getBillingNote(offer, showTrialMessaging)

                    return (
                      <Card
                        key={offer.priceId}
                        className={cn(
                          "flex h-full min-h-[24rem] flex-col overflow-hidden border-border/70 shadow-sm",
                          offer.highlight ? "border-emerald-500 shadow-lg shadow-emerald-100" : ""
                        )}
                      >
                        <CardHeader className="flex flex-col gap-3 border-b bg-muted/20 pb-3">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-2xl leading-none">{offer.name}</CardTitle>
                            {offer.highlight && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                Most Popular
                              </Badge>
                            )}
                            {isTrialCanceled && !offer.highlight && (
                              <Badge variant="outline">No free trial</Badge>
                            )}
                          </div>

                          <CardDescription className="leading-6">
                            {offer.description}
                          </CardDescription>

                          <div className="flex items-baseline gap-1.5">
                            <div className="text-xl font-semibold tracking-tight">
                              {formatPrice(offer.amount, offer.currency)}
                            </div>
                            <div className="text-sm text-muted-foreground">/month</div>
                          </div>
                        </CardHeader>

                        <CardContent className="flex flex-1 flex-col gap-5 py-5">
                          <div className="flex flex-1 flex-col gap-3">
                            {visibleFeatures.map((feature) => (
                              <div key={feature} className="flex items-start gap-3 text-sm">
                                <div className="mt-2 size-3 shrink-0 rounded-full bg-emerald-500" />
                                <span className="leading-6 text-foreground/80">{feature}</span>
                              </div>
                            ))}
                          </div>

                          <p className="mt-auto text-sm leading-7 text-muted-foreground">
                            {billingNote}
                          </p>
                        </CardContent>

                        <CardFooter className="pt-0">
                          <Button
                            type="button"
                            className={cn(
                              "w-full",
                              offer.highlight
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-foreground text-background hover:bg-foreground/90"
                            )}
                            onClick={() => void startCheckoutSession(offer)}
                            disabled={activeCheckoutPriceId !== null}
                          >
                            {isCheckingOut && (
                              <Loader2 data-icon="inline-start" className="animate-spin" />
                            )}
                            {showTrialMessaging ? "Start Trial" : "Choose plan"}
                          </Button>
                        </CardFooter>
                      </Card>
                    )
                  })}
                </div>
              )}

              {!isCatalogLoading &&
                !requiresPortal &&
                catalogOffers.length === 0 &&
                !errorMessage && (
                  <Alert>
                    <AlertTitle>{PAYMENT_COPY.noPlansTitle}</AlertTitle>
                    <AlertDescription>{PAYMENT_COPY.noPlansDescription}</AlertDescription>
                  </Alert>
                )}

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertTriangle className="size-4" />
                  <AlertTitle>{PAYMENT_COPY.genericErrorTitle}</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
